import { ZodError } from "zod";
import AiPromptSet from "../model/AiPromptSet";
import { BasicError, UserNotFoundError } from "../model/BasicError";
import { CartItem } from "../model/CartItem";
import { LockResult } from "../model/LockResult";
import { OrderDb } from "../model/OrderDb";
import { OrderItemDb } from "../model/OrderItemDb";
import { OrderItemUI } from "../model/OrderItemUI";
import { OrderUI } from "../model/OrderUI";
import { SearchResult } from "../model/SearchResult";
import { ShippingCost } from "../model/ShippingCost";
import { ShippingOption } from "../model/ShippingOption";
import { StoryBase } from "../model/StoryBase";
import { StoryDb } from "../model/StoryDb";
import { StoryUI } from "../model/StoryUI";
import { SupportRequest } from "../model/SupportRequest";
import { User } from "../model/User";
import { WsConnection } from "../model/WsConnection";

export interface Serializable {
	_className: string;
	_deserializeClassName: string;
	getIgnoreFields(): string[];
	get className(): string;
}

export interface SerializableError extends Serializable, Error {}

export class SerializingError extends Error implements SerializableError {
	_className: string = "SerializingError";
	_deserializeClassName: string = "SerializingError";
	name: string;
	message: string;
	stack?: string | undefined;
	constructor(name: string, message: string, stack?: string) {
		super(message);
		this.name = name;
		this.message = message;
		if (stack) this.stack = stack;
	}
	getIgnoreFields(): string[] {
		return [];
	}
	get className() {
		return this._className;
	}
}

export type SerializableType = object | Array<unknown> | Error | Date | Serializable | undefined | string | boolean;

export type serializerOptionsType = {
	stringify?: boolean;
	anonymiseFirstLevel?: boolean;
	secondLevelIgnoreFields?: string[];
	ignoreFields?: string[];
	ignoreUnrecognisedClasses?: boolean;
};

export const defaultSerializerOptions = {
	stringify: false,
	anonymiseFirstLevel: false,
	ignoreFields: [],
	secondLevelIgnoreFields: [],
	firstLevelIgnoreFields: [],
	ignoreUnrecognisedClasses: false,
};

export type deserializerOptionsType = {
	parse?: boolean;
	firstLevelClassName?: string;
	ignoreFields?: string[];
	anonymousFirstLevel?: boolean;
};

export const defaultDeserializerOptions = {
	parse: false,
	anonymousFirstLevel: false,
	ignoreFields: [],
};

export type SerializerProp = {
	_className: string;
	_deserializeClassName: string;
	clazz: new (...args: any[]) => Serializable;
};

//TODO refactor based on additional of deserialising field. Should be better typed.
export default class Serializer {
	types: { _deserializeClassName: string; _className: string; clazz: new () => Serializable }[] = [];
	standardTypes: (object | Array<unknown> | Error | Date)[] = [Object, Array, Error, Date, TypeError, ZodError];

	constructor(props?: Record<string, SerializerProp>) {
		if (props) {
			for (const [, value] of Object.entries(props)) {
				this.types.push({ _className: value._className, clazz: value.clazz, _deserializeClassName: value._deserializeClassName });
			}
		}
	}
	serialize(object: SerializableType, opts: serializerOptionsType = defaultSerializerOptions): string | object | Array<any> {
		const {
			ignoreFields,
			secondLevelIgnoreFields,
			ignoreUnrecognisedClasses,
			stringify,
			anonymiseFirstLevel,
		} = {...defaultSerializerOptions, ...opts};
		const primitive = object !== Object(object);
		const standardObject = object instanceof Object && this.isInstanceStandardType(object);
		const serializableObject = !primitive ? this.isInstanceSupported(object as Serializable) : false;
		if (serializableObject) ignoreFields.push(...(object as Serializable).getIgnoreFields());
		if (!primitive && !standardObject && !serializableObject && !ignoreUnrecognisedClasses)
			throw new SerializingError(
				"Invalid type",
				"type '" +
					(object as object).constructor.name +
					" " +
					((object as Serializable)._deserializeClassName || (object as Serializable)._className || "") +
					"' not serializable."
			);

		let tempObj: object = {};
		//If primitive, return itself
		if (!primitive) {
			for (const name of Object.getOwnPropertyNames(object)) {
				if (ignoreFields.includes(name)) continue;
				const obj: any = object as object;
				if (obj[name] !== null && obj[name] !== undefined && obj[name].constructor === Array) {
					(tempObj as any)[name] = ["Array", []];
					for (let x = 0; x < obj[name as keyof object].length; x++) {
						if (obj[name][x] !== undefined) {
							(tempObj as any)[name][1][x] = this.serialize(obj[name][x] as SerializableType, {
								...defaultSerializerOptions,
								stringify: false,
								anonymiseFirstLevel: false,
								ignoreFields: [...ignoreFields, ...secondLevelIgnoreFields],
							});
						} else {
							(tempObj as any)[name][1][x] = undefined;
						}
					}
				} else if (obj[name] !== null && obj[name] !== undefined && (obj[name].constructor === Function || obj[name] === Object(obj[name]))) {
					(tempObj as any)[name] = this.serialize(obj[name] as SerializableType, {
						...defaultSerializerOptions,
						stringify: false,
						anonymiseFirstLevel: false,
						ignoreFields: [...ignoreFields, ...secondLevelIgnoreFields],
					});
				} else {
					(tempObj as any)[name] = obj[name];
				}
			}
			if (!anonymiseFirstLevel) {
				const className = standardObject
					? (object as any).constructor.name
					: (object as Serializable)._deserializeClassName || (object as Serializable)._className;
				tempObj = [className, tempObj];
			}
		} else {
			tempObj = object as object;
		}
		if (stringify) {
			return JSON.stringify(tempObj);
		}
		return tempObj;
	}
	deserialize(input: any, opts: deserializerOptionsType = defaultDeserializerOptions) {
        const {parse, anonymousFirstLevel, ignoreFields, firstLevelClassName} = {...defaultDeserializerOptions, ...opts};
		let array;
		let className;
		let object;

		if (anonymousFirstLevel) {
			if (!firstLevelClassName)
				throw new SerializingError("Invalid parameter combination", "Anonymous first level, but no class name specified");
			className = firstLevelClassName;
			if (parse) {
				if (typeof input !== "string") throw new SerializingError("Invalid parameter combination", "Parse option true, but input is not string");
				object = JSON.parse(input);
			} else {
				object = { ...(input as object) };
			}
		} else {
			if (parse) {
				if (typeof input !== "string") throw new SerializingError("Invalid parameter combination", "Parse option true, but input is not string");
				array = JSON.parse(input);
			} else {
				array = [...(input as Array<any>)];
			}
			className = array[0];
			object = array[1];
		}
		let classObject: Serializable | Serializable[];
		let arr = false;
		if (this.isNameStandardType(className as string)) {
			const idx = this.getStandardTypeIndexByName(className as string);
			arr = this.standardTypes[idx] === Array;
			classObject = new (this.standardTypes[idx] as any)();
		} else if (this.isNameTypeSupported(className as string)) {
			const idx = this.getTypeIndexByName(className as string);
			const type = this.types[idx];
			classObject = new type.clazz();
			ignoreFields.push(...classObject.getIgnoreFields());
		} else {
			throw new SerializingError("Invalid type", "type  '" + className + "' not deserializable");
		}
		if (arr) {
			for (let x = 0; x < object.length; x++) {
				if (object[x] !== null && object[x] !== undefined && object[x].constructor === Array) {
					//@ts-expect-error
					classObject[x] = this.deserialize(object[x] as string | object, {
						...defaultDeserializerOptions,
						...ignoreFields,
					}) as Serializable[];
				} else {
					//@ts-expect-error
					classObject[x] = object[x];
				}
			}
		} else {
			for (const name of Object.getOwnPropertyNames(object)) {
				if (ignoreFields.includes(name)) continue;
				if (object[name] !== null && object[name] !== undefined && object[name].constructor === Array) {
					//@ts-expect-error
					classObject[name] = this.deserialize(object[name], { ...defaultDeserializerOptions, ...ignoreFields });
				} else {
					//@ts-expect-error
					classObject[name] = object[name];
				}
			}
		}
		return classObject;
	}
	isInstanceStandardType(object: any) {
		return this.standardTypes.findIndex((e: any) => e.name === object.constructor.name) !== -1;
	}
	isInstanceSupported(obj: unknown) {
		const object = obj as Serializable;
		if (object === undefined || (!object._className && !object._deserializeClassName && !this.isInstanceStandardType(object)))
			throw new BasicError(
				"Object is undefined or missing classname",
				"Cannot check if supported as object is undefined or missing classname." + object.constructor.name
			);
		const className = this.isInstanceStandardType(object) ? object.constructor.name : object._deserializeClassName || object._className;
		return (
			object instanceof BasicError || this.types.findIndex((e: any) => e._className === className || e._deserializeClassName === className) !== -1
		);
	}
	isNameStandardType(className: string) {
		return this.standardTypes.findIndex((e: any) => e.name === className) !== -1;
	}
	isNameTypeSupported(className: string) {
		return this.getTypeIndexByName(className) !== -1;
	}
	isTypeSupported(type: Serializable) {
		return this.getTypeIndex(type) !== -1;
	}
	getTypeIndex(type: Serializable) {
		return this.types.findIndex((e: SerializerProp) => e._className === type._className || e._className === type._deserializeClassName);
	}
	getTypeIndexByName(className: string) {
		return this.types.findIndex((e: SerializerProp) => e._className === className || e._deserializeClassName === className);
	}
	getStandardTypeIndexByName(className: string) {
		return this.standardTypes.findIndex((e: any) => e.name === className);
	}
}

export const serializationClassRegistry = {
	// Serialization overrides
	StoryBase: { _className: "Story", _deserializeClassName: "StoryBase", clazz: StoryBase },
	StoryUI: { _className: "Story", _deserializeClassName: "StoryUI", clazz: StoryUI },
	StoryDb: { _className: "Story", _deserializeClassName: "StoryDb", clazz: StoryDb },
	OrderDb: { _className: "Order", _deserializeClassName: "OrderDb", clazz: OrderDb },
	OrderUI: { _className: "Order", _deserializeClassName: "OrderUI", clazz: OrderUI },
	OrderItemDb: { _className: "OrderItem", _deserializeClassName: "OrderItemDb", clazz: OrderItemDb },
	OrderItemUI: { _className: "OrderItem", _deserializeClassName: "OrderItemUI", clazz: OrderItemUI },
	//Others
	SearchResult: { _className: "SearchResult", _deserializeClassName: "SearchResult", clazz: SearchResult },
	LockResult: { _className: "LockResult", _deserializeClassName: "LockResult", clazz: LockResult },
	WsConnection: { _className: "WsConnection", _deserializeClassName: "WsConnection", clazz: WsConnection },
	AiPromptSet: { _className: "AiPromptSet", _deserializeClassName: "AiPromptSet", clazz: AiPromptSet },
	BasicError: { _className: "BasicError", _deserializeClassName: "BasicError", clazz: BasicError },
	SerializingError: { _className: "SerializingError", _deserializeClassName: "SerializingError", clazz: SerializingError },
	CartItem: { _className: "CartItem", _deserializeClassName: "CartItem", clazz: CartItem },
	ShippingCost: { _className: "ShippingCost", _deserializeClassName: "ShippingCost", clazz: ShippingCost },
	ShippingOption: { _className: "ShippingOption", _deserializeClassName: "ShippingOption", clazz: ShippingOption },
	UserNotFoundError: { _className: "UserNotFoundError", _deserializeClassName: "UserNotFoundError", clazz: UserNotFoundError },
	User: { _className: "User", _deserializeClassName: "User", clazz: User },
	SupportRequest: {_className: "SupportRequest", _deserializeClassName: "SupportRequest", clazz: SupportRequest}
} as const;

type Registry = typeof serializationClassRegistry;

type RegistryEntry = Registry[keyof Registry];

export type ModelClassName = RegistryEntry["_className"];
export type ModelDeserialiseClassName = RegistryEntry["_deserializeClassName"];

export const getModelSerializer = () => {
	return new Serializer(serializationClassRegistry);
};
